import React from "react";
import { Box, CircularProgress, Tooltip } from "@mui/joy";
import dayjs from "dayjs";
import type { UserDocument, UserDocumentPatchPayload } from "api/users/User.typed";
import { useAuth } from "contexts/api";
import { useAlert } from "contexts/app/notificationToast";
import { NS_PER_DAY, warnDaysToGoDurationString } from "utils/userDocumentDuration";
import { useConfirmationModal } from "contexts/app/confirmation";
import DarkInput from "components/common/DarkInput";
import DarkDatePicker from "components/common/DarkDatePicker";
import DarkTypography from "components/common/DarkTypography";
import { OpenPdfInNewTab } from "utils/pdfViewer";
import { DEFAULT_PAGINATION_LIMIT } from "utils/paginationHelpers";
import Pagination from "components/common/Pagination";
import {
  SCloudUpload,
  SDelete,
  SEdit,
  SView,
  CloseIcon,
  CheckIcon,
  WarningIcon,
} from "../../../../styles/StyledMUI";
import IconButtonCTA from "components/common/IconButtonCTA";
import {
  userDocumentsAddHeadingRowSx,
  userDocumentsAddTitleTypographySx,
  userDocumentsDocumentListSx,
  userDocumentsEditActionIconSx,
  userDocumentsEditActionsRowSx,
  userDocumentsEditCheckIconSx,
  userDocumentsEditNameFieldSx,
  userDocumentsExpiryDateFieldSx,
  userDocumentsExpiryFieldClusterSx,
  userDocumentsExpiryFieldLabelSx,
  userDocumentsExpiryMutedLineSx,
  userDocumentsExpiryWarnIconSx,
  userDocumentsIconSlotSx,
  userDocumentsListItemRowSx,
  userDocumentsPaginationStripSx,
  userDocumentsRowActionsClusterSx,
  userDocumentsRowExpiryClusterSx,
  userDocumentsRowMetaRibbonSx,
  userDocumentsRowNameTypographySx,
  userDocumentsScrollAreaSx,
  userDocumentsSectionRootSx,
  userDocumentsUploadButtonIconSx,
  userDocumentsUploadExpiryAlertRowSx,
  userDocumentsUploadExpiryClusterSx,
  userDocumentsUploadNameFieldSx,
  userDocumentsUploadProgressSx,
  userDocumentsUploadRowShellSx,
  userDocumentsUploadTriggerClusterSx,
  userDocumentsUploadTriggerLabelSx,
  userDocumentsUploadTriggerWrapSx,
  userDocumentsUploadWarnClusterSx,
  userDocumentsUploadWarnDaysFieldSx,
  userDocumentsViewActionIconSx,
  userDocumentsWarnDaysFieldSx,
} from "styles/globalStyles";

import type { SxProps } from "@mui/joy/styles/types";

/** Shared file `SxProps` disagrees with some slot `sx` types (Joy progress vs MUI icons). */
function slotSx<T>(sx: SxProps): T {
  return sx as unknown as T;
}

const FILE_ACCEPT =
  "application/pdf,image/jpeg,image/png,image/gif,image/webp,image/bmp,image/heic,image/heif,.pdf,.jpg,.jpeg,.png,.gif,.webp";

type UserDocumentsSectionProps = {
  userId: number;
  canEdit: boolean;
};

function expiryDateToInputValue(expiresAt: string | null): string {
  if (!expiresAt) return "";
  const d = dayjs(expiresAt);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
}

function inputDateToRfc3339EndOfDay(dateStr: string): string {
  if (!dateStr.trim()) return "";
  return dayjs(dateStr).endOf("day").toISOString();
}

function warnBeforeToRoundedDays(ns: number | null | undefined): number | null {
  if (ns == null || ns <= 0 || !Number.isFinite(ns)) return null;
  return Math.round(ns / NS_PER_DAY);
}

function documentExpiryWarningLevel(doc: UserDocument): "expired" | "soon" | null {
  if (!doc.expires_at) return null;
  const exp = dayjs(doc.expires_at);
  if (!exp.isValid()) return null;
  const deadline = exp.endOf("day");
  const expired = deadline.isBefore(dayjs().startOf("day"));
  if (expired) return "expired";
  const wb = doc.warn_before;
  if (wb == null || wb <= 0 || !Number.isFinite(wb)) return null;
  const warnStart = deadline.subtract(wb / 1e6, "millisecond");
  if (!dayjs().isBefore(warnStart)) return "soon";
  return null;
}

export default function UserDocumentsSection({ userId, canEdit }: UserDocumentsSectionProps) {
  const { getUserDocuments, uploadUserDocument, patchUserDocument, deleteUserDocument } = useAuth();
  const modalContext = useConfirmationModal();
  const { showAlert } = useAlert();

  const [documents, setDocuments] = React.useState<UserDocument[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(DEFAULT_PAGINATION_LIMIT);
  const [total, setTotal] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [editName, setEditName] = React.useState("");
  const [editExpiryDate, setEditExpiryDate] = React.useState("");
  const [editWarnDays, setEditWarnDays] = React.useState("");

  const [uploadLabel, setUploadLabel] = React.useState("");
  const [uploadExpiryDate, setUploadExpiryDate] = React.useState("");
  const [uploadWarnDays, setUploadWarnDays] = React.useState("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = React.useState<number | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await getUserDocuments(userId, { page, limit });
      if (cancelled) return;
      setLoading(false);
      if (!res) {
        setDocuments([]);
        setTotal(0);
        setTotalPages(1);
        return;
      }
      const tp = Math.max(1, res.total_pages || (limit > 0 ? Math.ceil(res.total / limit) : 1));
      setDocuments(res.data);
      setTotal(res.total);
      setTotalPages(tp);
      if (res.data.length === 0 && page > tp) {
        setPage(tp);
      } else if (res.data.length === 0 && page > 1 && res.total > 0) {
        setPage((p) => p - 1);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getUserDocuments, userId, page, limit, refreshNonce]);

  const handleItemsPerPageChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const bumpRefresh = () => setRefreshNonce((n) => n + 1);

  const handleView = (doc: UserDocument) => {
    if (!doc.url) return;
    OpenPdfInNewTab(doc.url);
  };

  const startEdit = (doc: UserDocument) => {
    setEditingId(doc.id);
    setEditName(doc.name ?? "");
    setEditExpiryDate(expiryDateToInputValue(doc.expires_at));
    const wd = warnBeforeToRoundedDays(doc.warn_before ?? null);
    setEditWarnDays(wd != null && wd > 0 ? String(wd) : "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditExpiryDate("");
    setEditWarnDays("");
  };

  const saveEdit = async (docId: number) => {
    const name = editName.trim();
    const doc = documents.find((d) => d.id === docId);
    if (!doc) return;

    const payload: UserDocumentPatchPayload = {};
    if (name !== (doc.name ?? "")) payload.name = name;

    const prevDate = expiryDateToInputValue(doc.expires_at);
    const nextDate = editExpiryDate.trim();
    if (nextDate !== prevDate) {
      payload.expires_at = nextDate === "" ? null : inputDateToRfc3339EndOfDay(nextDate);
    }

    const tw = editWarnDays.trim();
    let nextWarnD: number | null;
    if (tw === "") {
      nextWarnD = null;
    } else {
      if (!/^\d+$/.test(tw)) {
        showAlert("Alerte (j) invalide", "warning");
        return;
      }
      const n = parseInt(tw, 10);
      if (!Number.isFinite(n) || n <= 0) {
        showAlert("Alerte (j) invalide", "warning");
        return;
      }
      nextWarnD = n;
    }
    const prevNs = doc.warn_before ?? null;
    const nextNs = nextWarnD == null ? null : Math.round(nextWarnD * NS_PER_DAY);
    const prevNsDayRounded =
      prevNs != null && prevNs > 0 ? Math.round(prevNs / NS_PER_DAY) * NS_PER_DAY : null;
    if (prevNsDayRounded !== nextNs) {
      payload.warn_before = nextNs;
    }

    if (Object.keys(payload).length === 0) {
      cancelEdit();
      return;
    }

    const updated = await patchUserDocument(userId, docId, payload);
    if (updated) {
      setDocuments((prev) => prev.map((d) => (d.id === docId ? updated : d)));
      cancelEdit();
    }
  };

  const handleDelete = async (doc: UserDocument) => {
    const ok = await modalContext.showConfirmation(
      "Confirmation",
      `Supprimer « ${doc.name || doc.url || "ce fichier"} » ?`,
    );
    if (!ok) return;
    setDeletingDocumentId(doc.id);
    try {
      await deleteUserDocument(userId, doc.id);
      if (editingId === doc.id) cancelEdit();
      bumpRefresh();
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const handleUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const label = uploadLabel.trim();
    if (!label) {
      showAlert("Indiquez un nom avant d’ajouter un document", "warning");

      return;
    }

    const formData = new FormData();
    formData.append("File", file);
    formData.append("Name", label);
    const exp = uploadExpiryDate.trim();
    if (exp) {
      formData.append("expires_at", inputDateToRfc3339EndOfDay(exp));
    }
    const wd = uploadWarnDays.trim();
    if (wd) {
      if (!/^\d+$/.test(wd)) {
        showAlert("Alerte (j) invalide", "warning");
        return;
      }
      const n = parseInt(wd, 10);
      if (!Number.isFinite(n) || n <= 0) {
        showAlert("Alerte (j) invalide", "warning");
        return;
      }
      const dur = warnDaysToGoDurationString(n);
      if (dur) formData.append("warn_before", dur);
    }

    setUploading(true);
    try {
      const created = await uploadUserDocument(userId, formData);
      if (created) {
        setUploadLabel("");
        setUploadExpiryDate("");
        setUploadWarnDays("");
        if (page !== 1) setPage(1);
        else bumpRefresh();
      }
    } finally {
      setUploading(false);
    }
  };

  const formatExpiryLine = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    const exp = dayjs(expiresAt);
    if (!exp.isValid()) return null;
    if (exp.endOf("day").isBefore(dayjs().startOf("day"))) return "Expiré";
    return exp.format("DD/MM/YYYY");
  };

  const uploadNameOk = uploadLabel.trim().length > 0;
  const deleteInProgress = deletingDocumentId !== null;
  const addDisabled = uploading || !uploadNameOk;
  const addTooltipTitle = uploading
    ? "Envoi…"
    : !uploadNameOk
      ? "Saisissez un nom pour ajouter un fichier"
      : "Ajouter document";

  return (
    <Box sx={userDocumentsSectionRootSx}>
      {canEdit && (
        <>
          <Box sx={userDocumentsAddHeadingRowSx}>
            <DarkTypography
              level="body-sm"
              colorVariant="title"
              sx={userDocumentsAddTitleTypographySx}
              component="h3"
            >
              Nouveau document
            </DarkTypography>
          </Box>
          <Box sx={userDocumentsUploadRowShellSx}>
            <DarkInput
              placeholder="Nom"
              size="sm"
              disabled={uploading}
              sx={userDocumentsUploadNameFieldSx}
              value={uploadLabel}
              onChange={(e) => setUploadLabel(e.target.value)}
            />
            <Box sx={userDocumentsUploadExpiryAlertRowSx}>
              <Box sx={userDocumentsUploadExpiryClusterSx}>
                <DarkTypography
                  level="body-xs"
                  colorVariant="secondary"
                  sx={userDocumentsExpiryFieldLabelSx}
                >
                  Date expiration
                </DarkTypography>
                <DarkDatePicker
                  value={uploadExpiryDate}
                  onChange={(v) => setUploadExpiryDate(v)}
                  aria-label="Date expiration"
                  disabled={uploading}
                  sx={userDocumentsExpiryDateFieldSx}
                />
              </Box>
              <Box sx={userDocumentsUploadWarnClusterSx}>
                <DarkTypography
                  level="body-xs"
                  colorVariant="secondary"
                  sx={userDocumentsExpiryFieldLabelSx}
                >
                  Alerte (j)
                </DarkTypography>
                <DarkInput
                  size="sm"
                  type="number"
                  disabled={uploading}
                  slotProps={{ input: { min: 1, step: 1 } }}
                  sx={userDocumentsUploadWarnDaysFieldSx}
                  value={uploadWarnDays}
                  onChange={(e) => setUploadWarnDays(e.target.value)}
                  aria-label="Nombre de jours avant alerte d’expiration"
                />
              </Box>
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept={FILE_ACCEPT}
              disabled={addDisabled}
              style={{ display: "none" }}
              onChange={handleUploadChange}
            />
            <Tooltip title={addTooltipTitle}>
              <Box component="span" sx={userDocumentsUploadTriggerWrapSx}>
                <Box sx={userDocumentsUploadTriggerClusterSx}>
                  <IconButtonCTA
                    ctaVariant="accent"
                    variant="plain"
                    size="sm"
                    onClick={() => !addDisabled && fileInputRef.current?.click()}
                    aria-label={
                      uploading ? "Envoi du fichier en cours" : "Choisir un fichier à ajouter"
                    }
                    disabled={addDisabled}
                    sx={userDocumentsIconSlotSx}
                  >
                    {uploading ? (
                      <CircularProgress size="sm" sx={slotSx(userDocumentsUploadProgressSx)} />
                    ) : (
                      <SCloudUpload sx={slotSx(userDocumentsUploadButtonIconSx)} />
                    )}
                  </IconButtonCTA>
                  <DarkTypography
                    level="body-sm"
                    colorVariant="title"
                    sx={userDocumentsUploadTriggerLabelSx}
                  >
                    Ajouter document
                  </DarkTypography>
                </Box>
              </Box>
            </Tooltip>
          </Box>
        </>
      )}

      <Box sx={userDocumentsScrollAreaSx}>
        {loading ? (
          <DarkTypography level="body-sm" colorVariant="muted">
            …
          </DarkTypography>
        ) : documents.length === 0 ? null : (
          <Box sx={userDocumentsDocumentListSx}>
            {documents.map((doc) => {
              const expLine = formatExpiryLine(doc.expires_at);
              const warnLevel = documentExpiryWarningLevel(doc);
              const documentDeleting = deletingDocumentId === doc.id;
              return (
                <Box key={doc.id} sx={userDocumentsListItemRowSx}>
                  {editingId === doc.id ? (
                    <>
                      <DarkInput
                        size="sm"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Nom"
                        sx={userDocumentsEditNameFieldSx}
                      />
                      <Box sx={userDocumentsExpiryFieldClusterSx}>
                        <DarkTypography
                          level="body-xs"
                          colorVariant="secondary"
                          sx={userDocumentsExpiryFieldLabelSx}
                        >
                          Date expiration
                        </DarkTypography>
                        <DarkDatePicker
                          value={editExpiryDate}
                          onChange={(v) => setEditExpiryDate(v)}
                          aria-label="Date expiration"
                          sx={userDocumentsExpiryDateFieldSx}
                        />
                      </Box>
                      <DarkInput
                        placeholder="Alerte (j)"
                        size="sm"
                        type="number"
                        slotProps={{ input: { min: 1, step: 1 } }}
                        sx={userDocumentsWarnDaysFieldSx}
                        value={editWarnDays}
                        onChange={(e) => setEditWarnDays(e.target.value)}
                      />
                      <Box sx={userDocumentsEditActionsRowSx}>
                        <Tooltip title="Annuler">
                          <IconButtonCTA
                            ctaVariant="danger"
                            size="sm"
                            variant="plain"
                            onClick={cancelEdit}
                            aria-label="Annuler"
                            sx={userDocumentsIconSlotSx}
                          >
                            <CloseIcon />
                          </IconButtonCTA>
                        </Tooltip>
                        <Tooltip title="OK">
                          <IconButtonCTA
                            ctaVariant="success"
                            size="sm"
                            variant="plain"
                            onClick={() => void saveEdit(doc.id)}
                            aria-label="Enregistrer"
                            sx={userDocumentsIconSlotSx}
                          >
                            <CheckIcon sx={slotSx(userDocumentsEditCheckIconSx)} />
                          </IconButtonCTA>
                        </Tooltip>
                      </Box>
                    </>
                  ) : (
                    <>
                      <DarkTypography level="title-sm" sx={userDocumentsRowNameTypographySx}>
                        {doc.name || "—"}
                      </DarkTypography>
                      <Box sx={userDocumentsRowMetaRibbonSx}>
                        <Box sx={userDocumentsRowExpiryClusterSx}>
                          {expLine ? (
                            <DarkTypography
                              level="body-xs"
                              colorVariant="muted"
                              sx={userDocumentsExpiryMutedLineSx}
                            >
                              {expLine}
                            </DarkTypography>
                          ) : null}
                          {warnLevel ? (
                            <Tooltip
                              title={
                                warnLevel === "expired" ? "Document expiré" : "Expiration proche"
                              }
                            >
                              <WarningIcon
                                sx={slotSx(userDocumentsExpiryWarnIconSx(warnLevel))}
                                aria-hidden
                              />
                            </Tooltip>
                          ) : null}
                        </Box>
                        <Box sx={userDocumentsRowActionsClusterSx}>
                          <Tooltip title="Ouvrir">
                            <IconButtonCTA
                              ctaVariant="neutral"
                              size="sm"
                              variant="plain"
                              onClick={() => handleView(doc)}
                              aria-label="Ouvrir le fichier"
                              sx={userDocumentsIconSlotSx}
                            >
                              <SView sx={slotSx(userDocumentsViewActionIconSx)} />
                            </IconButtonCTA>
                          </Tooltip>
                          {canEdit && (
                            <>
                              <Tooltip title="Éditer">
                                <IconButtonCTA
                                  ctaVariant="accent"
                                  size="sm"
                                  variant="plain"
                                  onClick={() => startEdit(doc)}
                                  aria-label="Éditer"
                                  disabled={deleteInProgress}
                                  sx={userDocumentsIconSlotSx}
                                >
                                  <SEdit sx={slotSx(userDocumentsEditActionIconSx)} />
                                </IconButtonCTA>
                              </Tooltip>
                              <Tooltip title={documentDeleting ? "Suppression…" : "Supprimer"}>
                                <IconButtonCTA
                                  ctaVariant="danger"
                                  size="sm"
                                  variant="plain"
                                  onClick={() => void handleDelete(doc)}
                                  aria-label={
                                    documentDeleting ? "Suppression en cours" : "Supprimer"
                                  }
                                  disabled={deleteInProgress}
                                  sx={userDocumentsIconSlotSx}
                                >
                                  {documentDeleting ? (
                                    <CircularProgress
                                      size="sm"
                                      sx={slotSx(userDocumentsUploadProgressSx)}
                                    />
                                  ) : (
                                    <SDelete />
                                  )}
                                </IconButtonCTA>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={userDocumentsPaginationStripSx}>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={total}
          itemsPerPage={limit}
          onPageChange={setPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          loading={loading}
        />
      </Box>
    </Box>
  );
}
